// src/utils/retry.js

/**
 * Retry a function with exponential backoff
 * @param {Function} fn - The function to retry
 * @param {Object} options - Retry options
 * @param {number} options.maxRetries - Maximum number of retry attempts (default: 3)
 * @param {number} options.initialDelay - Initial delay in ms (default: 1000)
 * @param {number} options.maxDelay - Maximum delay in ms (default: 10000)
 * @param {Function} options.shouldRetry - Function to determine if retry should be attempted
 * @returns {Promise<any>} - The result of the function
 */
export const withRetry = async (fn, options = {}) => {
    const {
        maxRetries = 3,
        initialDelay = 1000,
        maxDelay = 10000,
        shouldRetry = (error) => {
            // Retry on network errors, timeouts, and 5xx errors
            if (error.message?.includes('network')) return true;
            if (error.message?.includes('timeout')) return true;
            if (error.message?.includes('5')) return true;
            if (error.message?.includes('unavailable')) return true;
            if (error.message?.includes('deadline')) return true;
            return false;
        }
    } = options;

    let lastError;
    let delay = initialDelay;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            return await fn();
        } catch (error) {
            lastError = error;

            // Check if we should retry this error
            if (!shouldRetry(error)) {
                throw error;
            }

            // If this was the last attempt, throw the error
            if (attempt === maxRetries) {
                throw error;
            }

            // Calculate delay with exponential backoff and jitter
            const jitter = Math.random() * 0.3 * delay;
            const waitTime = Math.min(delay + jitter, maxDelay);

            console.warn(`Attempt ${attempt} failed, retrying in ${waitTime}ms...`, error.message);
            await new Promise(resolve => setTimeout(resolve, waitTime));

            // Increase delay for next attempt (exponential backoff)
            delay = Math.min(delay * 2, maxDelay);
        }
    }

    throw lastError;
};

/**
 * Retry with specific status code checks
 */
export const withRetryOnStatus = (fn, statusCodes = [429, 500, 502, 503, 504]) => {
    return withRetry(fn, {
        shouldRetry: (error) => {
            if (error.status && statusCodes.includes(error.status)) {
                return true;
            }
            if (error.code && statusCodes.includes(error.code)) {
                return true;
            }
            return false;
        }
    });
};

/**
 * Retry with exponential backoff for Firestore operations
 */
export const withFirestoreRetry = (fn) => {
    return withRetry(fn, {
        maxRetries: 5,
        initialDelay: 500,
        maxDelay: 15000,
        shouldRetry: (error) => {
            // Firestore specific retry conditions
            const message = error.message || '';
            const code = error.code || '';

            // Network and connection errors
            if (message.includes('network') || message.includes('connection')) return true;
            if (message.includes('timeout') || message.includes('deadline')) return true;
            if (message.includes('unavailable') || message.includes('unauthenticated')) return true;
            if (message.includes('resource exhausted')) return true;

            // Firestore specific error codes
            if (code === 'unavailable' || code === 'deadline-exceeded') return true;
            if (code === 'resource-exhausted' || code === 'aborted') return true;
            if (code === 'internal' || code === 'unknown') return true;

            // Don't retry on validation errors or permission errors
            if (message.includes('permission') || message.includes('validation')) return false;
            if (code === 'permission-denied' || code === 'invalid-argument') return false;
            if (message.includes('Insufficient stock')) return false;

            return false;
        }
    });
};

/**
 * Retry with circuit breaker pattern
 */
export class CircuitBreaker {
    constructor(options = {}) {
        this.failureThreshold = options.failureThreshold || 5;
        this.successThreshold = options.successThreshold || 3;
        this.timeout = options.timeout || 30000;
        this.failures = 0;
        this.successes = 0;
        this.state = 'CLOSED'; // CLOSED, OPEN, HALF_OPEN
        this.nextAttempt = null;
    }

    async execute(fn) {
        // Check if circuit is open
        if (this.state === 'OPEN') {
            if (this.nextAttempt && Date.now() < this.nextAttempt) {
                throw new Error('Circuit breaker is OPEN');
            }
            // Move to half-open state
            this.state = 'HALF_OPEN';
        }

        try {
            const result = await fn();
            this.onSuccess();
            return result;
        } catch (error) {
            this.onFailure();
            throw error;
        }
    }

    onSuccess() {
        if (this.state === 'HALF_OPEN') {
            this.successes++;
            if (this.successes >= this.successThreshold) {
                this.state = 'CLOSED';
                this.failures = 0;
                this.successes = 0;
            }
        } else {
            this.failures = 0;
        }
    }

    onFailure() {
        if (this.state === 'HALF_OPEN') {
            this.state = 'OPEN';
            this.failures = 0;
            this.successes = 0;
            this.nextAttempt = Date.now() + this.timeout;
            return;
        }

        this.failures++;
        if (this.failures >= this.failureThreshold) {
            this.state = 'OPEN';
            this.nextAttempt = Date.now() + this.timeout;
        }
    }

    reset() {
        this.failures = 0;
        this.successes = 0;
        this.state = 'CLOSED';
        this.nextAttempt = null;
    }
}